import { Regions } from "../data/regions";
import ISO from "iso-3166-2";
import {
  DEFAULT_COUNTRY_CODE,
  ISO3CountryCodesMap,
  SUPPORTED_PPS,
} from "../constants/locale";
import { CountryWithRegion, Language, LANGUAGE_VIEW } from "../types/countries";

export enum COUNTRY_USAGE {
  SITE_LANGUAGE = "SITE_LANGUAGE",
  EMAIL = "EMAIL",
}

export const replaceUnicodeWithSlash = (str: string) => {
  return str.replace(/&amp;#x2F;/g, "/");
};

export const UtilsLocale = {
  checkIfCountryIsFromRegion: (
    countryCode: string,
    countryList: any
  ): boolean => countryList.includes(countryCode),

  getCountryDataFromJson: ({
    countryCode = "",
    languageCode = "",
    languageView = LANGUAGE_VIEW.SITE_LANGUAGE,
  } = {}) => {
    let countryR: CountryWithRegion = {
      code: "",
      name: "",
      region: "",
      languages: [],
    };
    Regions.forEach((region) => {
      region.countries.forEach((country) => {
        if (countryCode === country.code) {
          countryR = { ...country, region: region.name };
        }
        return [];
      });

      return countryR;
    });

    countryR =
      (UtilsLocale.validateLanguageView(
        countryR,
        languageCode,
        languageView
      ) as CountryWithRegion) || null;

    return countryR || null;
  },

  validateCountry: (
    countryCode: string | any,
    usage = COUNTRY_USAGE.SITE_LANGUAGE
  ): string | null => {
    if (typeof countryCode === "string" && countryCode.length === 3) {
      try {
        const oRegion = ISO.subdivision(countryCode.toLowerCase());
        return oRegion?.countryCode.toLowerCase() ?? DEFAULT_COUNTRY_CODE;
      } catch (e: any) {
        console.warn(e, "Invalid country");
        return DEFAULT_COUNTRY_CODE;
      }
    }

    let isCountryValid: boolean = false;
    for (const region of Regions) {
      if (
        countryCode &&
        region.countries
          .map((country) => country.code)
          .includes(countryCode.toUpperCase())
      ) {
        isCountryValid = true;
        break;
      }
    }

    if (!isCountryValid) {
      if (usage === COUNTRY_USAGE.EMAIL) return null;
      return DEFAULT_COUNTRY_CODE;
    }

    return null;
  },

  validateLanguageView: (
    countryRes: CountryWithRegion | null,
    sLanguageCode: string,
    languageView: LANGUAGE_VIEW
  ) => {
    if (languageView === LANGUAGE_VIEW.SITE_LANGUAGE) {
      countryRes?.languages.map((cLanguage: Language) => {
        if (
          cLanguage.code === sLanguageCode &&
          cLanguage.languageView &&
          cLanguage.languageView === LANGUAGE_VIEW.PP_LANGUAGE
        ) {
          countryRes = null;
          return {};
        }
        return cLanguage;
      });
    }
    if (languageView === LANGUAGE_VIEW.PP_LANGUAGE) {
      countryRes?.languages.map((cLanguage: Language) => {
        if (
          cLanguage.code === sLanguageCode &&
          cLanguage.languageView &&
          cLanguage.languageView === LANGUAGE_VIEW.SITE_LANGUAGE
        ) {
          countryRes = null;
          return {};
        }
        return cLanguage;
      });
    }
    return countryRes;
  },

  convertISO3toISO2: (countryCode: string) => {
    const ISO2Code = ISO3CountryCodesMap[countryCode];
    return ISO2Code || countryCode;
  },

  convertISO2toISO3: (countryCode: string) => {
    const ISO3Code = Object.keys(ISO3CountryCodesMap).find(
      (key) => ISO3CountryCodesMap[key] === countryCode
    );

    return ISO3Code || countryCode;
  },

  hasPrivacyPolicy: (langCountryCode: string) => {
    return SUPPORTED_PPS.includes(langCountryCode);
  },

  /**
   * Get the PP (Privacy Policy) language for a given country code.
   * Finds the language with LANGUAGE_VIEW.PP_LANGUAGE, falling back to languages[0].
   * @param countryCode - The country code (e.g., "AL", "BJ", "FR")
   * @returns Object with languageCode and region, or null if country not found
   */
  getPPLanguageForCountry: (
    countryCode: string
  ): { languageCode: string; region: string } | null => {
    const countryData = UtilsLocale.getCountryDataFromJson({
      countryCode,
      languageView: LANGUAGE_VIEW.PP_LANGUAGE,
    }) as CountryWithRegion | null;

    if (!countryData?.languages || countryData.languages.length === 0) {
      return null;
    }

    // Find the PP_LANGUAGE variant, fall back to first language
    const ppLanguage =
      countryData.languages.find(
        (lang) => lang.languageView === LANGUAGE_VIEW.PP_LANGUAGE
      ) || countryData.languages[0];

    return {
      languageCode: ppLanguage.code,
      region: countryData.region,
    };
  },
};
